import { Component, signal, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter } from 'rxjs/operators';
import { NavbarComponent } from './components/navbar/navbar.component';
import { FooterComponent } from './components/footer/footer.component';
import { ToastComponent } from './components/toast/toast.component';
import { ConfirmationModalComponent } from './components/confirmation-modal/confirmation-modal.component';
import { ScrollLockService } from './core/scroll-lock.service';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, NavbarComponent, FooterComponent, ToastComponent, ConfirmationModalComponent],
  templateUrl: './app.html',
  styleUrl: './app.scss'
})
export class App {
  protected readonly title = signal('restlesssociety');
  protected readonly showGlobalFooter = signal(true);

  private readonly router = inject(Router);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly scrollLock = inject(ScrollLockService);

  constructor() {
    this.showGlobalFooter.set(!this.isEventDetailUrl(this.router.url));

    if (!isPlatformBrowser(this.platformId)) return;

    /**
     * Safety net: no modal should still be open after the URL has
     * actually changed, so any scroll-lock a modal took out (see
     * ScrollLockService) should always have been released by then. If
     * a modal component were ever destroyed in a way that skips its
     * ngOnDestroy (e.g. a bug, or an unexpected router edge case) the
     * lock could be left stuck — pinning <body> in place and clipping
     * the page to one screen's height, which looks exactly like "the
     * footer is sitting right under the navbar" and "the page won't
     * scroll" on the next page you land on. Force-clearing it here on
     * every completed navigation means that particular failure mode
     * can never outlive the page that caused it.
     */
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(() => this.scrollLock.reset());

    /**
     * App-wide scroll-to-top on every navigation.
     *
     * `provideRouter` is already configured with
     * `withInMemoryScrolling({ scrollPositionRestoration: 'top' })`
     * (see app.config.ts), which is *supposed* to handle this — but with
     * lazy-loaded routes (`loadComponent`) and client hydration, the
     * router's own scroll restoration can fire before the new route's
     * component has actually rendered, so it loses the race and the
     * page lands still scrolled to wherever the previous page left off.
     * That's why Home ended up with its own manual `window.scrollTo(0, 0)`
     * workaround. Centralizing the fix here at the root, on every
     * `NavigationEnd`, covers *every* route the same way — including
     * eagerly-loaded ones like /admin/login and lazy ones like
     * /admin/dashboard and /events — instead of relying on each page to
     * remember to do it itself.
     *
     * Fragment-based navigations (e.g. the "join the community" deep
     * link into #subscription-section) are left alone here — those are
     * handled by the destination page itself, which needs to wait for
     * its target element to exist before scrolling to it.
     */
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        this.showGlobalFooter.set(!this.isEventDetailUrl(event.urlAfterRedirects));
        const fragment = this.router.parseUrl(event.urlAfterRedirects).fragment;
        if (!fragment) {
          window.scrollTo(0, 0);
        }
      });
  }

  private isEventDetailUrl(url: string): boolean {
    return /^\/event\/[^/?#]+(?:[/?#]|$)/.test(url);
  }
}